import {
  DataDisplayWithAdornment,
  Icon,
  PillProps,
  colours,
} from "@validereinc/common-components";
import {
  WorkflowStatus,
  WorkflowStatusType,
  WorkflowTaskStatus,
  WorkflowTaskStatusType,
  WorkflowTaskType,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React from "react";

export const WorkflowStatusToPillVariantMap: Record<
  WorkflowStatusType,
  PillProps["variant"]
> = {
  [WorkflowStatus.IN_PROGRESS]: "info",
  [WorkflowStatus.COMPLETE]: "success",
  [WorkflowStatus.OVERDUE]: "warning",
  [WorkflowStatus.DISMISSED]: "default",
  [WorkflowStatus.MISSED]: "error",
  [WorkflowStatus.ERROR]: "error",
};

export const WorkflowTaskStatusToPillVariantMap: Record<
  WorkflowTaskStatusType,
  PillProps["variant"]
> = {
  [WorkflowTaskStatus.OPEN]: "info",
  [WorkflowTaskStatus.COMPLETE]: "success",
  [WorkflowTaskStatus.DISMISSED]: "default",
  [WorkflowTaskStatus.MISSED]: "error",
  [WorkflowTaskStatus.OVERDUE]: "warning",
  [WorkflowTaskStatus.ERROR]: "error",
};

export const WorkflowTaskPriorityIcon = ({
  priorityId,
  shouldRenderNoneLabel,
}: {
  priorityId: WorkflowTaskType["priority_id"];
  shouldRenderNoneLabel?: boolean;
}) => {
  switch (priorityId) {
    case 2:
      return (
        <Icon
          variant="caret-double-up"
          colour={colours.colours.status.error}
          alt="Highest priority icon"
          aria-roledescription="Highest priority icon"
          weight="bold"
          size={16}
        />
      );
    case 1:
      return (
        <Icon
          variant="caret-up"
          colour={colours.colours.status.warning}
          alt="High priority icon"
          aria-roledescription="High priority icon"
          weight="bold"
          size={16}
        />
      );
    case 0:
      return shouldRenderNoneLabel ? (
        <Icon
          variant="line-horizontal-one"
          colour={colours.colours.surface["300"]}
          alt="No priority icon"
          aria-roledescription="No priority icon"
          weight="bold"
          size={16}
        />
      ) : null;
    default:
      return null;
  }
};

export const WorkflowTaskPriorityDataDisplay = ({
  priorityId,
  priorityLabel,
  shouldRenderNoneLabel = true,
  isBusy,
  isLoading,
}: {
  priorityId: WorkflowTaskType["priority_id"];
  priorityLabel: WorkflowTaskType["priority_label"];
  shouldRenderNoneLabel?: boolean;
  isBusy?: boolean;
  isLoading?: boolean;
}) => {
  return (
    <DataDisplayWithAdornment
      showAdornment
      alignment="right"
      isInline
      isBusy={isBusy}
      isLoading={isLoading}
      adornmentSlot={() => {
        return (
          <WorkflowTaskPriorityIcon
            priorityId={priorityId}
            shouldRenderNoneLabel={shouldRenderNoneLabel}
          />
        );
      }}
      style={{ gap: "8px" }}
    >
      {priorityId === 0 && !shouldRenderNoneLabel
        ? "-"
        : toTitleCaseString(priorityLabel, { intent: "ui" })}
    </DataDisplayWithAdornment>
  );
};
